<template>
  <v-dialog v-model="opened" max-width="450" persistent>
    <v-btn class="close-btn" icon @click="opened = false">
      <v-icon size="27" :color="closeButtonColor">
        mdi-close-circle-outline
      </v-icon>
    </v-btn>

    <v-tabs-items v-if="opened" v-model="tab">
      <v-tab-item v-if="!onlyFiles">
        <v-date-picker
          v-model="dateInput"
          class="elevation-0"
          color="warning"
          header-color="#464646"
          :locale="$i18n.locale"
          :min="min"
          full-width
        />
        <div class="py-2 px-2">
          <v-btn
            elevation="0"
            color="warning"
            block
            :disabled="!dateInput"
            @click="tab++"
          >
            {{ $t('common.button.next') }}
          </v-btn>
        </div>
      </v-tab-item>

      <v-tab-item>
        <div class="pt-1 px-2 pb-2">
          <div class="d-flex align-center mb-4">
            <v-btn v-if="!onlyFiles" color="warning" icon @click="tab--">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>

            <h4 class="insurance-files__title">{{ this.$t('common.docs') }}</h4>
          </div>
          <multiple-file-loader
            :uploading="filesUploading"
            :downloading="downloading"
            :deleting-files="deletingFiles"
            :files="filesList"
            :read-only="readOnly"
            :no-download="noDownload"
            :no-delete="noDelete"
            @save="onSave"
            @download="onDownload"
            @delete="onDelete"
          />
        </div>
      </v-tab-item>
    </v-tabs-items>
  </v-dialog>
</template>

<script>
import MultipleFileLoader from '@/components/shared/MultipleFileLoader.vue';

export default {
  components: { MultipleFileLoader },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    onlyFiles: {
      type: Boolean,
      default: false,
    },
    type: {
      required: true,
    },
    date: {
      type: String,
      default: '',
    },
    autoId: {
      required: true,
    },
    files: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    noDownload: {
      type: Boolean,
      default: false,
    },
    noDelete: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    tab: 0,
    dateInput: '',
    filesList: [],
    filesUploading: false,
    downloading: false,
    deletingFiles: [],
  }),
  watch: {
    opened(val) {
      if (!val) {
        this.tab = 0;
        this.dateInput = '';
      } else {
        this.filesList = [...this.files];
        this.dateInput = this.date;
      }
    },
    files: {
      deep: true,
      handler(value) {
        this.filesList = [...value];
      },
    },
  },
  computed: {
    opened: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('change', val);
      },
    },
    closeButtonColor() {
      if (this.onlyFiles) return '#464646';
      return this.tab === 0 ? 'rgba(255,255,255,0.6)' : '#464646';
    },
    paramNames() {
      switch (this.type) {
        case 'kasko':
          return {
            date: 'expiryKasko',
            files: 'kaskoFiles',
          };

        case 'osgpo':
          return {
            date: 'expiryOsgpo',
            files: 'osgpoFiles',
          };

        default:
          return {};
      }
    },
    min() {
      return new Date().toISOString().substr(0, 10);
    },
  },
  methods: {
    createFilesFormData(files) {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append(`${this.paramNames.files}[]`, file);
      });

      return formData;
    },
    async onSave(files) {
      const data = {};

      if (this.dateInput) {
        data[this.paramNames.date] = this.dateInput;
      }

      this.filesUploading = true;
      const fileData = await this.$store.dispatch('auto/uploadFiles', {
        formData: this.createFilesFormData(files),
        autoId: this.autoId,
      });
      this.filesUploading = false;

      const isNew = this.files.every((file) => file.id !== fileData.id);

      if (isNew) {
        data[this.paramNames.files] = [fileData];
      }

      this.$emit('save', data);
      this.opened = false;
    },
    async onDownload(files) {
      this.downloading = true;

      // eslint-disable-next-line
      const requestList = files.map((file) => {
        return this.$store.dispatch('auto/downloadFile', {
          id: file.id,
          name: file.name,
        });
      });

      await Promise.all(requestList);

      this.downloading = false;
    },
    async onDelete(id) {
      this.deletingFiles = [...this.deletingFiles, id];

      const { success } = await this.$store.dispatch('auto/deleteFile', id);

      if (success) {
        this.$emit('delete', { id, filesName: [this.paramNames.files] });
      }

      this.deletingFiles = this.deletingFiles.filter((item) => item !== id);
    },
  },
};
</script>

<style lang="scss">
.insurance-files {
  &__title {
    font-size: 18px;
    color: #464646;
    margin: 5px;
  }
}
</style>
